import { Link as RouterLink } from 'react-router-dom';
import { resolveBuildRunNumber } from '@/features/builds/utils/buildRunNumber';
import { resolvePipelineSlug } from '@/features/pipelines/utils/pipelineSlug';
import { TableActionsCell } from '@/features/tests/components/testsTableCells';
import { Box, formatStatusLabel, getStatusTextColor, Link, Text } from '@/ui';
import { getStringFilterOperators } from '@/ui/composites/grid/gridOperators';
import { NameWithRunCell, RelativeTimeTooltipCell } from '@/ui/composites/table';
import { buildMetricColumns, STANDARD_METRIC_COLUMN_FLEX } from '@/ui/composites/table/tableUtils';
import type { GridColDef, GridRenderCellParams } from '@/ui/primitives';
import type { BuildStatus, MetricThresholdGroup, PipelineListItemDto } from '@flowqa/shared';

type PipelinesColumnsOptions = {
  selectedProjectId: string | null;
  thresholds: MetricThresholdGroup;
  resolveThresholds: (row: { projectId: string }) => MetricThresholdGroup | undefined;
  onStatusSelect?: (status: BuildStatus | 'none') => void;
  showDeltas?: boolean;
  includeActionsColumn?: boolean;
  onDeleteRow?: (row: PipelineListItemDto) => void;
  deleteRowPendingId?: string | null;
};

const columnFlex = {
  name: 1,
  latest: 0.5,
  lastFinished: 0.5,
  passRate: STANDARD_METRIC_COLUMN_FLEX,
  frequency: STANDARD_METRIC_COLUMN_FLEX,
  avgDuration: STANDARD_METRIC_COLUMN_FLEX,
  delta: 0.4,
  actions: 0.1,
};

export const buildPipelinesColumns = ({
  selectedProjectId,
  thresholds,
  resolveThresholds,
  onStatusSelect,
  showDeltas = true,
  includeActionsColumn = false,
  onDeleteRow,
  deleteRowPendingId = null,
}: PipelinesColumnsOptions): Array<GridColDef<PipelineListItemDto>> => {
  const stringOperators = getStringFilterOperators();
  const deltasEnabled = showDeltas;
  const resolvedThresholds = (row: PipelineListItemDto): MetricThresholdGroup => {
    if (selectedProjectId) return thresholds;
    return resolveThresholds(row) ?? thresholds;
  };
  const nameColumn: GridColDef<PipelineListItemDto> = {
    field: 'name',
    headerName: 'Pipeline',
    flex: columnFlex.name,
    filterOperators: stringOperators,
    renderCell: (params) => (
      <NameWithRunCell
        primary={{ label: params.row.name, href: `/pipelines/${resolvePipelineSlug(params.row)}` }}
      />
    ),
  };

  const latestStatusColumn: GridColDef<PipelineListItemDto> = {
    field: 'latestBuildStatus',
    headerName: 'Latest Status',
    flex: columnFlex.latest,
    filterable: false,
    sortable: false,
    align: 'left',
    headerAlign: 'left',
    renderCell: (params) => {
      const latestBuild = params.row.latestBuild ?? null;
      const status = latestBuild?.status ?? null;
      const isClickable = Boolean(status && onStatusSelect);
      const label = status ? formatStatusLabel(status) : 'No Builds';
      const runNumber = resolveBuildRunNumber(latestBuild?.buildKey ?? null);
      const pipelineSlug = resolvePipelineSlug(params.row);
      const buildHref =
        status && runNumber !== null ? `/pipelines/${pipelineSlug}/runs/${runNumber}` : null;
      const labelNode = buildHref ? (
        <Link
          component={RouterLink}
          to={buildHref}
          underline="hover"
          color="inherit"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <Text role="body" component="span" color="inherit">
            {label}
          </Text>
        </Link>
      ) : (
        label
      );
      if (isClickable && !buildHref) {
        return (
          <Box
            component="span"
            sx={{ cursor: 'pointer' }}
            onClick={() => {
              onStatusSelect?.(status ?? 'none');
            }}
          >
            <Text
              role="body"
              component="span"
              sx={{
                color: (theme) => getStatusTextColor(theme, status),
              }}
              transform="uppercase"
            >
              {labelNode}
            </Text>
          </Box>
        );
      }
      return (
        <Text
          role="body"
          component="span"
          sx={{
            color: (theme) => getStatusTextColor(theme, status),
          }}
          transform="uppercase"
        >
          {labelNode}
        </Text>
      );
    },
  };

  const lastFinishedColumn: GridColDef<PipelineListItemDto, Date | string | null> = {
    field: 'latestBuildTimestamp',
    headerName: 'Last Finished',
    flex: columnFlex.lastFinished,
    filterable: false,
    sortable: true,
    align: 'right',
    headerAlign: 'right',
    valueGetter: (_value, row) => row.latestBuild?.timestamp ?? null,
    renderCell: (params: GridRenderCellParams<PipelineListItemDto, Date | string | null>) => (
      <RelativeTimeTooltipCell value={params.value} />
    ),
  };

  const metricColumns = buildMetricColumns<PipelineListItemDto>({
    enabledKeys: ['passRate', 'frequency', 'avgDuration'],
    includeDeltas: deltasEnabled,
    fields: {
      passRate: 'metrics.passRate',
      frequency: 'metrics.frequencyPerDay',
      avgDuration: 'metrics.averageDurationMs',
    },
    deltaFields: {
      passRate: 'metrics.delta.passRate',
      frequency: 'metrics.delta.frequencyPerDay',
      avgDuration: 'metrics.delta.averageDurationMs',
    },
    flex: {
      passRate: columnFlex.passRate,
      frequency: columnFlex.frequency,
      avgDuration: columnFlex.avgDuration,
    },
    deltaFlex: {
      passRate: columnFlex.delta,
      frequency: columnFlex.delta,
      avgDuration: columnFlex.delta,
    },
    thresholds: thresholds,
    resolveThresholds: (row) => resolvedThresholds(row),
    align: 'right',
    headerAlign: 'right',
  });

  const actionsColumn: GridColDef<PipelineListItemDto> = {
    field: 'actions',
    headerName: '',
    flex: columnFlex.actions,
    minWidth: 56,
    filterable: false,
    sortable: false,
    disableColumnMenu: true,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: GridRenderCellParams<PipelineListItemDto>) => (
      <TableActionsCell
        rowName={params.row.name}
        showEditTagsAction={false}
        onDelete={() => {
          onDeleteRow?.(params.row);
        }}
        disableDelete={deleteRowPendingId === params.row.id}
      />
    ),
  };

  return [
    nameColumn,
    latestStatusColumn,
    lastFinishedColumn,
    ...metricColumns,
    ...(includeActionsColumn ? [actionsColumn] : []),
  ];
};
